import { useState } from "react";
import { faRobot, faArrowRight, faCommentDots } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge, BadgeProps } from "@/components/shadcn/badge";
import { Button } from "@/components/shadcn/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/shadcn/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/shadcn/select";
import { Skeleton } from "@/components/shadcn/skeleton";
import { Alert } from "@/components/common/Alert/Alert";
import { ConfirmDialog } from "@/components/common/Dialog";
import { LoadingOverlay } from "@/components/common/Loading/LoadingOverlay";
import { useLayout } from "@/contexts/LayoutContext";
import { showSuccessToast } from "@/hooks/use-toast";
import { updateAiAccessMode } from "../api";
import { AiSectionHeading } from "./AiSectionHeading";

import type { AiAccountSubscription } from "../types";

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

const limitFormatter = new Intl.NumberFormat(undefined);

const EXAMPLE_PROMPTS = [
  "Which customers have not been contacted this month?",
  "Give me a summary of our sales performance this quarter.",
  "Who are my top 5 customers by sales this year?",
  "Show me the contacts at our largest customer.",
];

const FEATURE_HIGHLIGHTS = [
  {
    title: "Ask naturally",
    description: "Ask questions about customers, companies, contacts and sales data in plain English.",
  },
  {
    title: "Understand performance",
    description: "Get instant sales performance and CRM summaries through chat.",
  },
  {
    title: "Explore historical data",
    description: "Get accurate, reliable insights into your data, instantly.",
  },
];

// Known values: "active" | "trial" | "inactive" | "expired" | "disabled" | "cancelled".
// Kept as string since the backend field is not a closed enum, so default to "disabled" styling
// (red) for anything unrecognised rather than assuming it's healthy.
const getStatusBadgeVariant = (subscriptionStatus: string): BadgeProps["variant"] => {
  const status = subscriptionStatus.toLowerCase();
  if (status === "active") {
    return "success";
  }
  if (status === "trial") {
    return "warning";
  }
  return "destructive";
};

type AiAccountSubscriptionCardProps = {
  subscription: AiAccountSubscription | null;
  isLoading: boolean;
  error: string | null;
  onAccessModeChanged: () => Promise<void>;
};

export const AiAccountSubscriptionCard = ({ subscription, isLoading, error, onAccessModeChanged }: AiAccountSubscriptionCardProps) => {
  const [confirmRestrictOpen, setConfirmRestrictOpen] = useState(false);
  const [isUpdatingAccessMode, setIsUpdatingAccessMode] = useState(false);
  const [accessModeError, setAccessModeError] = useState<string | null>(null);

  const { data: layoutData } = useLayout();
  const interestSubject = `klipboard CRM - Vecta AI - Registering interest (Account ${layoutData.account})`;
  const interestBody = `Hi, I'm ${layoutData.username} and I'd like to register my interest in Vecta AI for account ${layoutData.account}. Please keep me informed about availability.`;
  const interestMailto = `mailto:jayne.hill@klipboard.com?subject=${encodeURIComponent(interestSubject)}&body=${encodeURIComponent(interestBody)}`;

  const handleAccessModeChange = async (grantAllUsers: boolean) => {
    setIsUpdatingAccessMode(true);
    setAccessModeError(null);
    try {
      await updateAiAccessMode(grantAllUsers);
      showSuccessToast("Access mode updated successfully.");
      await onAccessModeChanged();
    } catch (err) {
      console.error("Error updating AI access mode: ", err);
      setAccessModeError("Failed to update access mode.");
    } finally {
      setIsUpdatingAccessMode(false);
      setConfirmRestrictOpen(false);
    }
  };

  const handleAccessModeSelect = (value: string) => {
    if (value === "allowlist") {
      setConfirmRestrictOpen(true);
      return;
    }
    handleAccessModeChange(true);
  };

  return (
    <div className="min-w-0">
      <LoadingOverlay isVisible={isUpdatingAccessMode} message="Updating access mode..." />
      {(isLoading || error || subscription) && (
        <AiSectionHeading
          title="Account subscription"
          subtitle={!error ? "AI availability and allowance for this account." : undefined}
        />
      )}
      {error && <Alert variant="error" persistent>{error}</Alert>}
      {!error && isLoading && (
        <dl className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => (
            <div key={index}>
              <Skeleton className="h-4 w-24" />
              <Skeleton className="mt-2 h-6 w-32" />
            </div>
          ))}
        </dl>
      )}
      {!error && !isLoading && !subscription && (
        <Card className="border-0 shadow-none bg-muted/20">
          <CardContent className="flex flex-col gap-6 p-8">
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-kPurple-100 text-kPurple-500">
                  <FontAwesomeIcon icon={faRobot} aria-hidden="true" />
                </span>
                <span className="text-xs font-semibold text-kPurple-500">VECTA AI</span>
                <Badge variant="warning">Coming soon</Badge>
              </div>
              <h3 className="text-2xl font-semibold">Unlock AI for your team</h3>
              <p className="text-sm text-muted-foreground max-w-xl">
                Ask Vecta questions about your CRM data and get instant answers, right where you work.
              </p>
              <Button asChild className="w-fit bg-kPurple-500 text-white hover:bg-kPurple-600 hover:text-white">
                <a href={interestMailto} target="_blank" rel="noopener noreferrer">
                  Register your interest
                  <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                </a>
              </Button>
            </div>

            <p className="text-sm font-medium">What you can do with Vecta AI</p>

            <div className="grid gap-4 sm:grid-cols-3">
              {FEATURE_HIGHLIGHTS.map((feature) => (
                <Card key={feature.title} className="shadow-none bg-background">
                  <CardHeader className="p-4 pb-2">
                    <CardTitle className="text-sm font-semibold text-kPurple-500">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0 text-sm text-muted-foreground">
                    {feature.description}
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium">Ask questions in plain English</p>
              <ul className="flex flex-col gap-2">
                {EXAMPLE_PROMPTS.map((prompt) => (
                  <li key={prompt} className="flex items-start gap-2">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-kPurple-100 text-kPurple-500">
                      <FontAwesomeIcon icon={faCommentDots} className="text-xs" />
                    </span>
                    <span className="rounded-2xl rounded-tl-sm border border-input bg-background px-3 py-2 text-sm text-muted-foreground">
                      {prompt}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-sm text-muted-foreground">
              Chat with Vecta AI anytime, directly inside klipboard CRM.
            </p>
          </CardContent>
        </Card>
      )}
      {!error && !isLoading && subscription && (
        <dl className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-sm text-muted-foreground">Status</dt>
            <dd className="mt-1">
              <Badge variant={getStatusBadgeVariant(subscription.subscriptionStatus)}>
                {subscription.subscriptionStatus.charAt(0).toUpperCase() + subscription.subscriptionStatus.slice(1)}
              </Badge>
            </dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">Token limit</dt>
            <dd className="mt-1 text-lg font-semibold">
              {subscription.usageLimit === null ? "Unlimited" : limitFormatter.format(subscription.usageLimit)}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">Effective from</dt>
            <dd className="mt-1 font-medium">{dateFormatter.format(new Date(subscription.effectiveFromUtc))}</dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">Effective to</dt>
            <dd className="mt-1 font-medium">
              {subscription.effectiveToUtc ? dateFormatter.format(new Date(subscription.effectiveToUtc)) : "No end date"}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">Access mode</dt>
            <dd className="mt-1">
              <Select
                value={subscription.grantAllUsers ? "all" : "allowlist"}
                onValueChange={handleAccessModeSelect}
                disabled={isUpdatingAccessMode || !["active", "trial"].includes(subscription.subscriptionStatus.toLowerCase())}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All users</SelectItem>
                  <SelectItem value="allowlist">Allowlist only</SelectItem>
                </SelectContent>
              </Select>
              {accessModeError && <p className="mt-1 text-sm text-destructive">{accessModeError}</p>}
            </dd>
          </div>
        </dl>
      )}

      {!error && !isLoading && subscription && <hr className="pb-4 mt-4" />}

      <ConfirmDialog
        open={confirmRestrictOpen}
        onOpenChange={setConfirmRestrictOpen}
        hideTrigger
        title="Restrict AI access to an allowlist?"
        description={
          <>
            Only users explicitly granted access in the User access list below will be able to use AI. All other users in
            this account will immediately lose access.
          </>
        }
        confirmText="Restrict access"
        cancelText="Cancel"
        loading={isUpdatingAccessMode}
        onConfirm={() => handleAccessModeChange(false)}
        disableConfirm={isUpdatingAccessMode}
      />
    </div>
  );
};
